// This file is auto-generated by @hey-api/openapi-ts

import type { Client, ClientMeta, Options as Options2, RequestResult, TDataShape } from './client';
import { client } from './client.gen';
import type { BrowseData, BrowseErrors, BrowseResponses, CreateTokenData, CreateTokenErrors, CreateTokenResponses, CreateUserData, CreateUserErrors, CreateUserResponses, DeleteFileData, DeleteFileErrors, DeleteFileResponses, DeleteUserData, DeleteUserErrors, DeleteUserResponses, ListSharesData, ListSharesErrors, ListSharesResponses, ListTokensData, ListTokensErrors, ListTokensResponses, ListUsersData, ListUsersErrors, ListUsersResponses, LoginData, LoginErrors, LoginResponses, LogoutData, LogoutErrors, LogoutResponses, MeData, MeErrors, MeResponses, RefreshSessionData, RefreshSessionErrors, RefreshSessionResponses, RevokeShareData, RevokeShareErrors, RevokeShareResponses, RevokeTokenData, RevokeTokenErrors, RevokeTokenResponses, ShareFolderData, ShareFolderErrors, ShareFolderResponses, SignDownloadData, SignDownloadErrors, SignDownloadResponses, SignUploadData, SignUploadErrors, SignUploadResponses, StatFileData, StatFileErrors, StatFileResponses, UpdateUserData, UpdateUserErrors, UpdateUserResponses } from './types.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean, TResponse = unknown> = Options2<TData, ThrowOnError, TResponse> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta;
};

/**
 * Sign in with email and password
 *
 * Returns an access token for the Authorization header and a refresh token to renew it. Accounts are created by an admin.
 */
export const login = <ThrowOnError extends boolean = false>(options: Options<LoginData, ThrowOnError>): RequestResult<LoginResponses, LoginErrors, ThrowOnError> => (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: '/auth/login',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * End the session a refresh token belongs to
 */
export const logout = <ThrowOnError extends boolean = false>(options: Options<LogoutData, ThrowOnError>): RequestResult<LogoutResponses, LogoutErrors, ThrowOnError> => (options.client ?? client).post<LogoutResponses, LogoutErrors, ThrowOnError>({
    url: '/auth/logout',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Trade a refresh token for fresh tokens
 *
 * The refresh token is single use: the response carries its replacement.
 */
export const refreshSession = <ThrowOnError extends boolean = false>(options: Options<RefreshSessionData, ThrowOnError>): RequestResult<RefreshSessionResponses, RefreshSessionErrors, ThrowOnError> => (options.client ?? client).post<RefreshSessionResponses, RefreshSessionErrors, ThrowOnError>({
    url: '/auth/refresh',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Who I am, and the folders shared with me
 */
export const me = <ThrowOnError extends boolean = false>(options?: Options<MeData, ThrowOnError>): RequestResult<MeResponses, MeErrors, ThrowOnError> => (options?.client ?? client).get<MeResponses, MeErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/me',
    ...options
});

/**
 * List the shares on this shelf
 */
export const listShares = <ThrowOnError extends boolean = false>(options?: Options<ListSharesData, ThrowOnError>): RequestResult<ListSharesResponses, ListSharesErrors, ThrowOnError> => (options?.client ?? client).get<ListSharesResponses, ListSharesErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shares',
    ...options
});

/**
 * Share a folder with a user
 *
 * Sharing a folder the user already holds changes the permission. A share covers every file under the folder, including those uploaded later.
 */
export const shareFolder = <ThrowOnError extends boolean = false>(options: Options<ShareFolderData, ThrowOnError>): RequestResult<ShareFolderResponses, ShareFolderErrors, ThrowOnError> => (options.client ?? client).post<ShareFolderResponses, ShareFolderErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shares',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Revoke a share
 */
export const revokeShare = <ThrowOnError extends boolean = false>(options: Options<RevokeShareData, ThrowOnError>): RequestResult<RevokeShareResponses, RevokeShareErrors, ThrowOnError> => (options.client ?? client).delete<RevokeShareResponses, RevokeShareErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shares/{id}',
    ...options
});

/**
 * List one folder of the shelf
 *
 * The root of a user who is not an admin lists the folders shared with them. A folder above a share lists only the way down to it.
 */
export const browse = <ThrowOnError extends boolean = false>(options?: Options<BrowseData, ThrowOnError>): RequestResult<BrowseResponses, BrowseErrors, ThrowOnError> => (options?.client ?? client).get<BrowseResponses, BrowseErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shelf',
    ...options
});

/**
 * Sign a URL to preview or download one file
 *
 * The URL reaches the bucket directly and expires; the server never carries the file.
 */
export const signDownload = <ThrowOnError extends boolean = false>(options: Options<SignDownloadData, ThrowOnError>): RequestResult<SignDownloadResponses, SignDownloadErrors, ThrowOnError> => (options.client ?? client).post<SignDownloadResponses, SignDownloadErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shelf/download',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Describe one file
 *
 * Not shared with you and not there answer the same 404.
 */
export const statFile = <ThrowOnError extends boolean = false>(options: Options<StatFileData, ThrowOnError>): RequestResult<StatFileResponses, StatFileErrors, ThrowOnError> => (options.client ?? client).get<StatFileResponses, StatFileErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shelf/file',
    ...options
});

/**
 * Delete one file
 */
export const deleteFile = <ThrowOnError extends boolean = false>(options: Options<DeleteFileData, ThrowOnError>): RequestResult<DeleteFileResponses, DeleteFileErrors, ThrowOnError> => (options.client ?? client).delete<DeleteFileResponses, DeleteFileErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shelf/files',
    ...options
});

/**
 * Sign a URL to upload one file
 *
 * PUT the file's bytes to the URL. An existing file at the key is replaced. Needs write on the folder.
 */
export const signUpload = <ThrowOnError extends boolean = false>(options: Options<SignUploadData, ThrowOnError>): RequestResult<SignUploadResponses, SignUploadErrors, ThrowOnError> => (options.client ?? client).post<SignUploadResponses, SignUploadErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/shelf/upload',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * List my API tokens
 */
export const listTokens = <ThrowOnError extends boolean = false>(options?: Options<ListTokensData, ThrowOnError>): RequestResult<ListTokensResponses, ListTokensErrors, ThrowOnError> => (options?.client ?? client).get<ListTokensResponses, ListTokensErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tokens',
    ...options
});

/**
 * Create an API token
 *
 * The token is in this answer and nowhere else: only its hash is kept. It acts as the caller, bounded by the caller's shares.
 */
export const createToken = <ThrowOnError extends boolean = false>(options: Options<CreateTokenData, ThrowOnError>): RequestResult<CreateTokenResponses, CreateTokenErrors, ThrowOnError> => (options.client ?? client).post<CreateTokenResponses, CreateTokenErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tokens',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Revoke an API token
 *
 * Your own, or anybody's for an admin.
 */
export const revokeToken = <ThrowOnError extends boolean = false>(options: Options<RevokeTokenData, ThrowOnError>): RequestResult<RevokeTokenResponses, RevokeTokenErrors, ThrowOnError> => (options.client ?? client).delete<RevokeTokenResponses, RevokeTokenErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tokens/{id}',
    ...options
});

/**
 * List the users of this shelf
 */
export const listUsers = <ThrowOnError extends boolean = false>(options?: Options<ListUsersData, ThrowOnError>): RequestResult<ListUsersResponses, ListUsersErrors, ThrowOnError> => (options?.client ?? client).get<ListUsersResponses, ListUsersErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/users',
    ...options
});

/**
 * Give an address access
 *
 * Creates the account it signs in with, with the password given; there is no self-registration.
 */
export const createUser = <ThrowOnError extends boolean = false>(options: Options<CreateUserData, ThrowOnError>): RequestResult<CreateUserResponses, CreateUserErrors, ThrowOnError> => (options.client ?? client).post<CreateUserResponses, CreateUserErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/users',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Remove a user and their shares
 */
export const deleteUser = <ThrowOnError extends boolean = false>(options: Options<DeleteUserData, ThrowOnError>): RequestResult<DeleteUserResponses, DeleteUserErrors, ThrowOnError> => (options.client ?? client).delete<DeleteUserResponses, DeleteUserErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/users/{id}',
    ...options
});

/**
 * Promote or demote a user
 */
export const updateUser = <ThrowOnError extends boolean = false>(options: Options<UpdateUserData, ThrowOnError>): RequestResult<UpdateUserResponses, UpdateUserErrors, ThrowOnError> => (options.client ?? client).patch<UpdateUserResponses, UpdateUserErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/users/{id}',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});
